import formatRFC3339 from 'date-fns/formatRFC3339';
import fromUnixTime from 'date-fns/fromUnixTime';

import { shouldDisableLogs, sinatraOrigin, datasetMaxBytes } from '../config';
import keycloakService from '../keycloak_service';

import get_topic_name from '../utils/get_topic_name';

const DATASET_QUERY_PARAMS = `dataset[]=*&dataset.separator=.&dataset.max_bytes=${datasetMaxBytes}`

interface LegacyFormatLogsResponseType {
  records: string[],
  next: number | null,
}

export type AuditLogEntryType = {
  unixTime: number,
  formattedDateTime: string,
  logData: any
}

export type ParsedLogsResponse = {
  logs: AuditLogEntryType[],
  nextPageToken: number | null,
}

const parseLegacyLogsResponse = (response: LegacyFormatLogsResponseType): ParsedLogsResponse => ({
  logs: response.records.map((data: string): AuditLogEntryType => {
    const parsed = JSON.parse(data);
    const time = parsed.time / 1000;
    return {
      unixTime: time,
      formattedDateTime: formatRFC3339(fromUnixTime(time)),
      logData: { 'audit_data': parsed },
    };
  }),
  nextPageToken: response.next,
})

const parseArrowLogsResponse = (response: object[]): ParsedLogsResponse => ({
  logs: response.map((data: any): AuditLogEntryType => {
    const time = data.time ? data.time / 1000 : -1;
    return {
      unixTime: time,
      formattedDateTime: formatRFC3339(fromUnixTime(time)),
      logData: { 'audit_data': data },
    };
  }),
  nextPageToken: null,
})

const postToApi = (sinatraEndpoint = '', fetchOptions = {}, parseResponseFunc: Function = () => {}) => fetch(`${sinatraOrigin}/${sinatraEndpoint}`, fetchOptions)
  .then((response) => {
    if (!response.ok) {
      throw Error(response?.statusText || 'An Unexpected error has occurred.')
    }
    return response.json()
  })
  .then((response: Response) => parseResponseFunc(response))

export type LogQueryArgs = [pipelineID: string, workspaceId: number | null, startDate: number, endDate: number, useArrowLogFormat: boolean, locations: string[] | null, nextPageToken: number | null, useAnomalyLogs: boolean | null]

export const pullAuditLogsForPipeline = async (queryParams: LogQueryArgs) => {
  const [
    pipelineID,
    workspaceId,
    startDate,
    endDate,
    useArrowLogFormat,
    locationNames,
    nextPageToken,
    useAnomalyLogs,
  ] = queryParams;

  if (shouldDisableLogs) {
    return { logs: [], nextPageToken: null };
  }

  const topic_data = await get_topic_name(workspaceId, pipelineID);
  const topic = topic_data?.topic_name;
  const defaultPipelineTopic = `pipeline-${pipelineID}-inference`;

  const formattedStartDate = formatRFC3339(fromUnixTime(startDate));
  const formattedEndDate = formatRFC3339(fromUnixTime(endDate));
  const dateParams = `time.start=${encodeURIComponent(formattedStartDate)}&time.end=${encodeURIComponent(formattedEndDate)}`
  const pageSize = 50;

  const locationStr = locationNames?.map((name, index) => `&partition_filter[${index}]=${name}`)?.join('');

  const url = topic
    ? `v1/logs/topic/${topic}${useAnomalyLogs ? '-failures' : ''}/records?limit=${pageSize}&${dateParams}&${DATASET_QUERY_PARAMS}`
    : `v1/logs/topic/${defaultPipelineTopic}${useAnomalyLogs ? '-failures' : ''}/records?limit=${pageSize}&${dateParams}&${DATASET_QUERY_PARAMS}`;

  const edgeAwareUrl = locationStr ? `${url}${locationStr}` : url;

  const fetchOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/json${useArrowLogFormat ? '; format=pandas-records' : ''}`,
      'Authorization': keycloakService.getAuthHeader()
    },
    body: JSON.stringify(nextPageToken || {})
  };

  return postToApi(edgeAwareUrl, fetchOptions, useArrowLogFormat ? parseArrowLogsResponse : parseLegacyLogsResponse);
};

export default pullAuditLogsForPipeline;
